<template>
    <button :class="className" :disabled="disabled || isLoading" :type="type" @click="click">
        <SpinnerIcon v-if="isLoading" class="infinite-rotation" />
        <span v-if="hasAction" class="sbtn__loader">Loading...</span>
        <slot v-else />
    </button>
</template>

<script>
import SpinnerIcon from '@/components/Icon/Spinner.vue';

export default {
    name: 'StyledButton',

    components: {
        SpinnerIcon,
    },
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        format: {
            type: String,
            required: false,
            default: 'primary',
        },
        hasAction: {
            type: Boolean,
            required: false,
            default: false,
        },
        type: {
            type: String,
            required: false,
            default: '',
        },
        size: {
            type: String,
            required: false,
            default: 'medium',
        },
        isLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            hover: false,
        };
    },

    computed: {
        className() {
            const className = `sbtn sbtn-${this.format} sbtn-${this.size}`;

            if (!this.hasAction) {
                return className;
            }

            return `${className} sbtn--action`;
        },
    },

    methods: {
        click($event) {
            this.$emit('click', $event);
        },
    },
};
</script>

<style>
@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.infinite-rotation {
    animation: rotate 0.4s linear infinite;
}

.sbtn {
    border-radius: 4px;
    font-size: 14px;
    line-height: 17px;
    padding: 12px 36px;
    transition-duration: 300ms;
    transition-timing-function: ease;
    transition-property: background-color, color, font-weight;
}

.sbtn-small {
    font-size: 12px;
    line-height: 15px;
    padding: 8px 24px;
}

.sbtn-medium {
    font-size: 14px;
    line-height: 17px;
    padding: 12px 36px;
}

.sbtn-large {
    font-size: 16px;
    line-height: 19px;
    padding: 16px 48px;
}

.sbtn--action {
    padding-bottom: 8px;
    padding-top: 8px;
}

.sbtn-delete {
    background-color: var(--delete-button-default-bg);
    border: 0;
    color: var(--delete-button-default-color);
    font-weight: 700;
}

.sbtn-delete:hover {
    background-color: var(--delete-button-hover-bg);
}

.sbtn-delete:disabled {
    background-color: var(--delete-button-inactive-bg);
    color: var(--delete-button-inactive-color);
}

.sbtn-delete:focus,
.sbtn-delete:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
}

.sbtn-delete:focus,
.sbtn-delete:not(:disabled):not(.disabled):active {
    background-color: var(--delete-button-press-bg);
}

.sbtn-delete-outline {
    background-color: var(--color-white);
    border: 1px solid var(--delete-button-default-bg);
    font-weight: 400;
}

.sbtn-delete-outline:hover {
    background-color: var(--delete-button-default-bg);
    color: var(--color-white);
}

.sbtn-delete-outline:disabled {
    background-color: var(--color-white);
    border-color: var(--delete-button-inactive-bg);
    color: var(--delete-button-inactive-bg);
}

.sbtn-delete-outline:focus,
.sbtn-delete-outline:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
}

.sbtn-primary {
    background-color: var(--primary-button-default-bg);
    border: 0;
    color: #191919;
    font-weight: 700;
}

.sbtn-primary:hover {
    background-color: var(--primary-button-hover-bg);
}

.sbtn-primary:disabled {
    background-color: var(--primary-button-inactive-bg);
    color: var(--primary-button-inactive-color);
}

.sbtn-primary:focus,
.sbtn-primary:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
}

.sbtn-primary:focus,
.sbtn-primary:not(:disabled):not(.disabled):active {
    background-color: var(--primary-button-hover-bg);
}

.sbtn-primary-outline {
    background-color: var(--color-white);
    border: 1px solid var(--primary-button-default-bg);
    font-weight: 400;
}

.sbtn-primary-outline:hover {
    background-color: var(--primary-button-default-bg);
    color: var(--color-white);
}

.sbtn-primary-outline:disabled {
    background-color: var(--color-white);
    border-color: var(--primary-button-inactive-bg);
    color: var(--primary-button-inactive-bg);
}

.sbtn-primary-outline:focus,
.sbtn-primary-outline:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
}

.sbtn-secondary {
    background-color: var(--secondary-button-default-bg);
    border: 0;
    color: var(--secondary-button-default-color);
    font-weight: 400;
}

.sbtn-secondary:hover {
    background-color: var(--secondary-button-hover-bg);
    color: var(--secondary-button-default-color);
    font-weight: 700;
}

.sbtn-secondary:disabled {
    background-color: var(--secondary-button-inactive-bg);
    color: var(--secondary-button-inactive-color);
    font-weight: 700;
}

.sbtn-secondary:focus,
.sbtn-secondary:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
}

.sbtn-secondary:focus,
.sbtn-secondary:not(:disabled):not(.disabled):active {
    background-color: var(--secondary-button-press-bg);
    font-weight: 700;
}

.sbtn-cancel {
    background-color: #ffffff;
    color: var(--button-style-labels-button-text-label, #121212);
    border: 1px solid var(--Button-Borders-Color, #ffac2f);
}

.sbtn-cancel:hover {
    background-color: #f5f5f5;
}

.sbtn-cancel:focus {
    background-color: #f5f5f5;
    box-shadow: none;
}

.sbtn-cancel:disabled {
    background-color: #f5f5f5;
    border: 1px solid var(--Button-Borders-Color, #ffac2f);
}

.sbtn-cancel:focus,
.sbtn-cancel:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
}

.sbtn-dropdown {
    background-color: var(--dropdown-button-default-bg);
    border: 1px solid var(--dropdown-button-border-color);
    color: var(--dropdown-button-default-color);
    font-weight: 700;
}

.sbtn-dropdown:hover {
    background-color: var(--dropdown-button-hover-bg);
    border-color: var(--dropdown-button-hover-bg);
}

.sbtn-dropdown:focus {
    background-color: var(--dropdown-button-press-bg);
    border-color: var(--dropdown-button-press-bg);
    color: var(--dropdown-button-press-color);
    box-shadow: none;
}

.sbtn__loader {
    border-radius: 50%;
    height: 7em;
    width: 7em;
}

.sbtn__loader {
    animation: load8 1.1s infinite linear;
    border-top: 1.1em solid rgba(255, 255, 255, 0.2);
    border-right: 1.1em solid rgba(255, 255, 255, 0.2);
    border-bottom: 1.1em solid rgba(255, 255, 255, 0.2);
    border-left: 1.1em solid var(--color-white);
    display: inline-block;
    font-size: 3px;
    overflow: hidden;
    position: relative;
    text-indent: -9999px;
    transform: translateZ(0);
}

@keyframes load8 {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
